import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";

export const Route = createFileRoute("/app/vocabulary_trainer_new")({
  component: VocabularyTrainingForm,
});

interface FormInputs {
  text_of_last_days: number;
  maximum_streak: number;
  total_vocabularies: number;
  known_vocabularies: number;
}

function VocabularyTrainingForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      text_of_last_days: 20,
      known_vocabularies: 5,
      total_vocabularies: 5,
      maximum_streak: 2,
    },
  });
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    navigate({
      to: "/app/vocabulary_training_mode",
      search: {
        knownVocabularies: data.known_vocabularies,
        totalVocabularies: data.total_vocabularies,
        maxStreak: data.maximum_streak,
        textAge: data.text_of_last_days,
      },
    });
  };

  return (
    <div className="max-w-md mx-auto p-4 bg-white shadow-md rounded-md">
      <h1 className="text-2xl font-bold mb-4">Vocabulary Training Settings</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <label
            htmlFor="text_of_last_days"
            className="block text-sm font-medium"
          >
            Only include vocabulary of texts created in the last X days
          </label>
          <input
            type="number"
            id="text_of_last_days"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            {...register("text_of_last_days", {
              required: "This field is required",
            })}
          />
          {errors.text_of_last_days && (
            <p className="text-red-500 text-sm">
              {errors.text_of_last_days.message || "invalid"}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="maximum_streak" className="block text-sm font-medium">
            Omit Vocabulary with a winning streak larger than this.
          </label>
          <input
            type="number"
            id="maximum_streak"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            {...register("maximum_streak", {
              required: "This field is required",
            })}
          />
          {errors.maximum_streak && (
            <p className="text-red-500 text-sm">
              {errors.maximum_streak.message || "invalid"}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="known_vocabularies"
            className="block text-sm font-medium"
          >
            This many vocabularies that were seen before
          </label>
          <input
            type="number"
            id="known_vocabularies"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            {...register("known_vocabularies", {
              required: "This field is required",
              min: 0,
            })}
          />
          {errors.known_vocabularies && (
            <p className="text-red-500 text-sm">
              {errors.known_vocabularies.message || "invalid"}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="total_vocabularies"
            className="block text-sm font-medium"
          >
            This many vocabularies per page in total
          </label>
          <input
            type="number"
            id="total_vocabularies"
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            {...register("total_vocabularies", {
              required: "This field is required",
              min: 0,
              validate: {
                validateGreaterEqualThanKnown: (
                  _: number,
                  formValues: FormInputs,
                ) => {
                  return (
                    formValues.known_vocabularies <=
                      formValues.total_vocabularies ||
                    "Must be greater than known vocabularies"
                  );
                },
              },
            })}
          />
          {errors.total_vocabularies && (
            <p className="text-red-500 text-sm">
              {errors.total_vocabularies.message || "invalid"}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600"
        >
          Start Training
        </button>
      </form>
    </div>
  );
}
